import React, { createContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

export const AppContext = createContext();

// セッションストレージのキー
const STORAGE_KEY = "appContextData";

export const AppProvider = ({ children }) => {
  const location = useLocation();
  const [state, setState] = useState({
    globalLoading: true, // ローディング中
    globalMedicalInstitutionId: null, // 病院ID(表示用)
    globalMedicalInstitutionName: null, // 病院名(表示用)
    globalReservationMenus: null, // 診療科一覧
    globalSelectMenu: null, // 選択診療科
    globalQuestionnaireToken: null, // 問診トークン
    globalSelectAppointment: null, // 選択初再診
    globalSelectAccountVerify: null, // アカウントの有無
    globalDatetimeSelectMode: null, // 日時変更画面モード(null or change)
    globalSelectReservationDateTime: null, // 選択予約日時
    globalSelectReservationInfo: null, // 選択予約情報
    globalLoginUserInfo: null, // 受診者一覧
    globalSelectPatient: null, // 選択受診者
    globalReservationToken: null, // 選択予約ID
    globalReservationDetail: null, // 予約情報
    globalReservationInput: null, // 予約入力情報
    globalReservationInputForm: null, // 予約入力フォーム
    globalQuestionnaire: null, // 問診情報
    globalAnswerInformation: null, // 回答
    globalEmailAddress: null, // メールアドレス
    previousPath: null, // 直前のパスを保存するためのフィールド
    globalFamilyInfo: null, // 家族情報一覧
    globalAccessToken: null, // アクセストークン
  });

  // 初回マウント時にセッションストレージからデータを復元
  useEffect(() => {
    const savedData = sessionStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setState(parsedData); // 全体を復元

        // 復元後にセッションストレージをクリア
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error(
          "Failed to parse appContext data from sessionStorage:",
          error
        );
      }
      setGlobalLoading(false);
    } else {
      setGlobalLoading(false);

      if (
        state.globalMedicalInstitutionId === null &&
        window.location.pathname.includes("mypage")
      ) {
        window.location.href = "/mypage/account-login";
      }
    }
  }, []);

  // ページがリロードまたは閉じる直前にセッションストレージにstateを保存
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && state) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            ...state,
            globalLoading: true,
          })
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [state]);

  // locationの変更を監視してpreviousPathを更新
  useEffect(() => {
    return () => {
      setState((prevState) => ({
        ...prevState,
        previousPath: location.pathname,
      }));
    };
  }, [location.pathname]);

  // 各種setter関数
  const setGlobalLoading = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalLoading: value,
    }));
  };

  const setGlobalMedicalInstitutionId = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalMedicalInstitutionId: value,
    }));
  };

  const setGlobalMedicalInstitutionName = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalMedicalInstitutionName: value,
    }));
  };

  const setGlobalReservationMenus = (value) => {
    setState((prevState) => ({ ...prevState, globalReservationMenus: value }));
  };

  const setGlobalSelectMenu = (value) => {
    setState((prevState) => ({ ...prevState, globalSelectMenu: value }));
  };

  const setGlobalQuestionnaireToken = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalQuestionnaireToken: value,
    }));
  };

  const setGlobalSelectAppointment = (value) => {
    setState((prevState) => ({ ...prevState, globalSelectAppointment: value }));
  };

  const setGlobalSelectAccountVerify = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalSelectAccountVerify: value,
    }));
  };

  const setGlobalDatetimeSelectMode = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalDatetimeSelectMode: value,
    }));
  };

  const setGlobalSelectReservationDateTime = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalSelectReservationDateTime: value,
    }));
  };

  const setGlobalSelectReservationInfo = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalSelectReservationInfo: value,
    }));
  };

  const setGlobalLoginUserInfo = (value) => {
    setState((prevState) => ({ ...prevState, globalLoginUserInfo: value }));
  };

  const setGlobalSelectPatient = (value) => {
    setState((prevState) => ({ ...prevState, globalSelectPatient: value }));
  };

  const setGlobalReservationToken = (value) => {
    setState((prevState) => ({ ...prevState, globalReservationToken: value }));
  };

  const setGlobalReservationDetail = (value) => {
    setState((prevState) => ({ ...prevState, globalReservationDetail: value }));
  };

  const setGlobalReservationInput = (value) => {
    setState((prevState) => ({ ...prevState, globalReservationInput: value }));
  };

  const setGlobalReservationInputForm = (value) => {
    setState((prevState) => ({
      ...prevState,
      globalReservationInputForm: value,
    }));
  };

  const setGlobalQuestionnaire = (value) => {
    setState((prevState) => ({ ...prevState, globalQuestionnaire: value }));
  };

  const setGlobalAnswerInformation = (value) => {
    setState((prevState) => ({ ...prevState, globalAnswerInformation: value }));
  };

  const setGlobalEmailAddress = (value) => {
    setState((prevState) => ({ ...prevState, globalEmailAddress: value }));
  };

  const setGlobalFamilyInfo = (value) => {
    setState((prevState) => ({ ...prevState, globalFamilyInfo: value }));
  };

  const setGlobalAccessToken = (value) => {
    setState((prevState) => ({ ...prevState, globalAccessToken: value }));
  };
  return (
    <AppContext.Provider
      value={{
        ...state,
        setGlobalLoading,
        setGlobalMedicalInstitutionId,
        setGlobalMedicalInstitutionName,
        setGlobalReservationMenus,
        setGlobalSelectMenu,
        setGlobalQuestionnaireToken,
        setGlobalSelectAppointment,
        setGlobalSelectAccountVerify,
        setGlobalDatetimeSelectMode,
        setGlobalSelectReservationDateTime,
        setGlobalSelectReservationInfo,
        setGlobalLoginUserInfo,
        setGlobalSelectPatient,
        setGlobalReservationToken,
        setGlobalReservationDetail,
        setGlobalReservationInput,
        setGlobalReservationInputForm,
        setGlobalQuestionnaire,
        setGlobalAnswerInformation,
        setGlobalEmailAddress,
        setGlobalFamilyInfo,
        setGlobalAccessToken,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
